import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { type KeyboardEvent, useEffect, useRef } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';

type ConversationSearchBarProps = {
    readonly query: string;
    readonly onQueryChange: (query: string) => void;
    readonly matchCount: number;
    // Zero-based index of the active match; ignored when there are no matches.
    readonly activeMatchIndex: number;
    readonly onNextMatch: () => void;
    readonly onPreviousMatch: () => void;
    readonly onClose: () => void;
};

// Ctrl+F-style search bar for the open conversation: type a keyword, then step
// through matches with the up/down controls (or Enter / Shift+Enter). Shows the
// "current of total" position so the user knows where they are in the chat.
export function ConversationSearchBar({
    query,
    onQueryChange,
    matchCount,
    activeMatchIndex,
    onNextMatch,
    onPreviousMatch,
    onClose,
}: ConversationSearchBarProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const hasQuery = query.trim().length > 0;
    const hasMatches = matchCount > 0;

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            if (!hasMatches) {
                return;
            }
            if (event.shiftKey) {
                onPreviousMatch();
            } else {
                onNextMatch();
            }
        }
    };

    return (
        <div className="bg-background flex w-full items-center gap-2 rounded-md border px-2 py-1.5 shadow-sm">
            <Search className="text-muted-foreground h-4 w-4 shrink-0" />
            <Input
                ref={inputRef}
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search this chat…"
                className="h-8 min-w-0 flex-1 border-0 px-0 shadow-none focus-visible:ring-0"
            />
            <span className="text-muted-foreground w-16 shrink-0 text-right text-xs tabular-nums">
                {hasQuery
                    ? hasMatches
                        ? `${activeMatchIndex + 1} of ${matchCount}`
                        : 'No results'
                    : ''}
            </span>
            <Button
                aria-label="Previous match"
                className="h-7 w-7"
                disabled={!hasMatches}
                onClick={onPreviousMatch}
                size="icon"
                type="button"
                variant="ghost"
            >
                <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
                aria-label="Next match"
                className="h-7 w-7"
                disabled={!hasMatches}
                onClick={onNextMatch}
                size="icon"
                type="button"
                variant="ghost"
            >
                <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
                aria-label="Close search"
                className="h-7 w-7"
                onClick={onClose}
                size="icon"
                type="button"
                variant="ghost"
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
}
